import * as React from "react";
import "./styles/paginator.css";

export interface IPaginatorProps extends React.Props<PaginatorComponent> {
  totalNumberOfRecords: number;
  recordsPerPage: number;
  numberOfPageLinks: number;
  currentPageNumber: number;
  includeRecordsPerPageDropdown?: boolean;
  recordsPerPageOptions?: number[];
  showPagerEvenIfOnlyOnlyOnePageOfResults?: boolean;

  text_currentlyDisplayingPage: string;
  text_displayPage: string;
  text_displayPreviousPageOfResults: string;
  text_displayPreviousSetOfPageLinks: string;
  text_displayNextPageOfResults: string;
  text_displayNextSetOfPageLinks: string;
  text_noNextPageOfResults: string;
  text_noNextSetOfPageLinks: string;
  text_noPreviousPageOfResults: string;
  text_noPreviousSetOfPageLinks: string;
  text_of: string;
  text_perPage?: string; // Only required if includeRecordsPerPageDropdown is true
  text_ofResultsText: string;

  onChangePage: (pageNumber: number) => any;
  onChangeRecordsPerPage?: (recordsPerPage: number) => any;
}

export default class PaginatorComponent extends React.Component<IPaginatorProps, {}> {

  private numberOfPages: number = 0;
  private currentPageSet: number = 0;
  private numberOfPageSets: number = 0;
  private firstPageLinkDisplayed: number = 0;
  private lastPageLinkDisplayed: number = 0;
  private firstRecordDisplayed: number = 0;
  private lastRecordDisplayed: number = 0;

  constructor(props) {
    super(props);

    // Bind functions:
    this.setPageDisplayed = this.setPageDisplayed.bind(this);
    this.setRecordsPerPage = this.setRecordsPerPage.bind(this);
    this.displayPreviousPage = this.displayPreviousPage.bind(this);
    this.displayPreviousPageSet = this.displayPreviousPageSet.bind(this);
    this.displayNextPage = this.displayNextPage.bind(this);
    this.displayNextPageSet = this.displayNextPageSet.bind(this);
    this.killEvent = this.killEvent.bind(this);
  }

  public render() {

    this.numberOfPages = Math.ceil(this.props.totalNumberOfRecords / this.props.recordsPerPage);
    this.numberOfPageSets = Math.ceil(this.numberOfPages / this.props.numberOfPageLinks);
    this.currentPageSet = Math.ceil(this.props.currentPageNumber / this.props.numberOfPageLinks);
    this.firstRecordDisplayed = ((this.props.currentPageNumber - 1) * this.props.recordsPerPage) + 1;
    this.lastRecordDisplayed = Math.min(this.firstRecordDisplayed + (this.props.recordsPerPage - 1), this.props.totalNumberOfRecords);
    this.firstPageLinkDisplayed = ((this.currentPageSet - 1) * this.props.numberOfPageLinks) + 1;
    this.lastPageLinkDisplayed = Math.min((this.currentPageSet * this.props.numberOfPageLinks), this.numberOfPages);

    const leftArrow: JSX.Element = this.getLeftArrow();
    const leftEllipsis: JSX.Element = this.getLeftEllipsis();
    const pageLinks: JSX.Element[] = this.getPageLinks();
    const blankLinks: JSX.Element[] = this.getBlankLinks();
    const rightEllipsis: JSX.Element = this.getRightEllipsis();
    const rightArrow: JSX.Element = this.getRightArrow();

    const pagerOptions: any[] = [];
    if (this.props.includeRecordsPerPageDropdown) {
      const pagerOptionNumbers = [...this.props.recordsPerPageOptions];
      pagerOptionNumbers.sort((a, b) => a - b);

      pagerOptionNumbers.forEach((p) => {
        pagerOptions.push(<option value={p} key={p}>{p} {this.props.text_perPage}</option>);
      });
    }

    return (
      <div className="notranslate">
        {this.showPager() &&
          <div className="PO-paging">
            <ul className="PO-pageButton">

              {leftArrow}
              {leftEllipsis}

              {pageLinks.map((pageLink, i) => pageLink)}

              {blankLinks.map((blankLink, i) => blankLink)}

              {rightEllipsis}
              {rightArrow}

            </ul>

            <div className="PO-pageCount">{this.firstRecordDisplayed}-{this.lastRecordDisplayed} {this.props.text_of} {this.props.totalNumberOfRecords}</div>

            {this.props.includeRecordsPerPageDropdown &&
              <div className="PO-pageSize">
                <select className="PO-pageSizeDropdown" value={this.props.recordsPerPage} onChange={this.setRecordsPerPage}>
                  {pagerOptions}
                </select>
              </div>
            }

          </div>
        }
      </div>

    );
  }

  private showPager = (): boolean => {
    if (this.props.showPagerEvenIfOnlyOnlyOnePageOfResults) { return true; }
    if (this.numberOfPages > 1) { return true; }
  }

  private getLeftArrow(): JSX.Element {
    if (this.props.currentPageNumber > 1) {
      return <li><a href="#" onClick={this.displayPreviousPage} aria-label={this.props.text_displayPreviousPageOfResults}><em className="fa fa-arrow-left"></em></a></li>;
    } else {
      return <li className="inactive"><a href="#" onClick={this.killEvent} aria-label={this.props.text_noPreviousPageOfResults}><em className="fa fa-arrow-left"></em></a></li>;
    }
  }

  private getLeftEllipsis(): JSX.Element {
    if (this.currentPageSet > 1) {
      return <li><a href="#" onClick={this.displayPreviousPageSet} aria-label={this.props.text_displayPreviousSetOfPageLinks}>...</a></li>;
    } else {
      return null;
    }
  }

  private getPageLinks(): JSX.Element[] {
    const pageLinks: JSX.Element[] = [];
    let ariaLabel: string = "";
    let anchorClassName: string = "";
    let ariaCurrent: boolean = false;
    for (let p: number = this.firstPageLinkDisplayed; p <= this.lastPageLinkDisplayed; p++) {
      ariaLabel = (p === this.props.currentPageNumber) ? this.props.text_currentlyDisplayingPage + " " + p + " " + this.props.text_ofResultsText : this.props.text_displayPage + " " + p + " " +
        this.props.text_ofResultsText;
      anchorClassName = "";
      ariaCurrent = false;
      if (p.toString() === this.props.currentPageNumber.toString()) {
        anchorClassName = "selected";
        ariaCurrent = true;
      }
      pageLinks.push(<li key={p}><a href="#" onClick={this.setPageDisplayed} className={anchorClassName} aria-current={ariaCurrent} aria-label={ariaLabel} data-page-number={p}>{p}</a></li>);
    }
    return pageLinks;
  }

  private getBlankLinks(): JSX.Element[] {
    // Add blank links at end of series of pages (if required... keeps ellipsis and arrows aligned across all page sets).
    const blankLinks: JSX.Element[] = [];

    if (this.numberOfPageSets > 1) {
      const numLinksDisplayed = (this.lastPageLinkDisplayed - this.firstPageLinkDisplayed + 1);
      if (numLinksDisplayed < this.props.numberOfPageLinks) {
        for (let p = 0; p < this.props.numberOfPageLinks - numLinksDisplayed; p++) {
          const blankLink = <li aria-hidden="true" key={"100" + p}><span aria-hidden="true">&nbsp;&nbsp;</span></li>;
          blankLinks.push(blankLink);
        }
      }
    }

    return blankLinks;
  }

  private getRightEllipsis(): JSX.Element {
    if (this.currentPageSet < this.numberOfPageSets) {
      return <li><a href="#" onClick={this.displayNextPageSet} aria-label={this.props.text_displayNextSetOfPageLinks}>...</a></li>;
    } else {
      return null;
    }
  }

  private getRightArrow(): JSX.Element {
    if (this.props.currentPageNumber < this.numberOfPages) {
      return <li><a href="#" onClick={this.displayNextPage} aria-label={this.props.text_displayNextPageOfResults}><em className="fa fa-arrow-right"></em></a></li>;
    } else {
      return <li className="inactive"><a href="#" onClick={this.killEvent} aria-label={this.props.text_noNextPageOfResults}><em className="fa fa-arrow-right"></em></a></li>;
    }
  }

  // Event handlers:

  private setPageDisplayed(e): void {
    e.preventDefault();
    const pageNumber: number = parseInt(e.target.attributes["data-page-number"].value, 10);
    this.props.onChangePage(pageNumber);
  }

  private setRecordsPerPage(e): void {
    e.preventDefault();
    const recordsPerPage: number = parseInt(e.target.value, 10);
    this.props.onChangeRecordsPerPage(recordsPerPage);
  }

  private displayPreviousPage(e): void {
    e.preventDefault();
    this.props.onChangePage(this.props.currentPageNumber - 1);
  }

  private displayPreviousPageSet(e): void {
    e.preventDefault();
    const pageNumber: number = ((this.currentPageSet - 1) * this.props.numberOfPageLinks);
    this.props.onChangePage(pageNumber);
  }

  private displayNextPage(e): void {
    e.preventDefault();
    this.props.onChangePage(this.props.currentPageNumber + 1);
  }

  private displayNextPageSet(e): void {
    e.preventDefault();
    const pageNumber: number = ((this.currentPageSet * this.props.numberOfPageLinks) + 1);
    this.props.onChangePage(pageNumber);
  }

  private killEvent(e): void {
    e.preventDefault();
  }

}
